import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  uploadImage,
  deleteImage,
  adminOperation,
  deleteUserFromAuth,
} from '../../utils/supabase';
import type {
  DonationCampaignType as DonationCampaign,
  AdoptionCaseType as AdoptionCase,
  ShelterType as Shelter,
  UserType as User,
  AdoptionRequestType as AdoptionRequest,
  ShelterEventType as ShelterEvent,
} from '../../utils/supabase';
import SubscriptionManager from '../../components/SubscriptionManager';
import SubscriptionNotification from '../../components/SubscriptionNotification';
import PaymentRequired from '../../components/PaymentRequired';
import { checkSubscriptionStatus } from '../../utils/subscription';

export default function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('donaciones');
  const [donationCases, setDonationCases] = useState<DonationCampaign[]>([]);
  const [adoptionCases, setAdoptionCases] = useState<AdoptionCase[]>([]);
  const [shelters, setShelters] = useState<Shelter[]>([]);
  const [shelterEvents, setShelterEvents] = useState<ShelterEvent[]>([]);
  const [adoptionRequests, setAdoptionRequests] = useState<AdoptionRequest[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [modalType, setModalType] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({
    email: '',
    phone: '',
    city: '',
    state: ''
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [trialEndDate, setTrialEndDate] = useState<string | null>(null);

  /* ------------------------------------------------------------------------
   * Session handling
   * --------------------------------------------------------------------- */
  const checkSessionValidity = () => {
    const sessionData = localStorage.getItem('user_session');
    if (!sessionData) return null;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      // 3‑hour expiry
      if (now > session.expiresAt) {
        localStorage.removeItem('user_session');
        return null;
      }
      return session;
    } catch {
      localStorage.removeItem('user_session');
      return null;
    }
  };

  // Check subscription status
  const checkUserSubscriptionStatus = async (userId: string) => {
    try {
      console.log('🔍 [SUBSCRIPTION] Starting subscription check for user:', userId);
      setSubscriptionLoading(true);
      
      // Get subscription info from utility function (includes trial end date)
      console.log('🔍 [SUBSCRIPTION] Calling checkSubscriptionStatus utility...');
      const subscriptionInfo = await checkSubscriptionStatus(userId);
      console.log('📊 [SUBSCRIPTION] Utility function result:', subscriptionInfo);
      
      if (subscriptionInfo) {
        setSubscriptionStatus(subscriptionInfo.subscriptionStatus);
        setTrialEndDate(subscriptionInfo.trialEndDate);
        console.log('✅ [SUBSCRIPTION] Subscription status set:', {
          subscriptionStatus: subscriptionInfo.subscriptionStatus,
          trialEndDate: subscriptionInfo.trialEndDate,
          isActive: subscriptionInfo.isActive,
          isTrialActive: subscriptionInfo.isTrialActive,
          isSubscriptionActive: subscriptionInfo.isSubscriptionActive
        });
      } else {
        console.log('❌ [SUBSCRIPTION] No subscription info returned from utility');
      }
      
      // Also check with server API for additional info
      console.log('🔍 [SUBSCRIPTION] Calling server API for additional info...');
      const response = await fetch('https://huellitas-unidas-server.vercel.app/api/check-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid: userId }),
      });
      
      const data = await response.json();
      console.log('📊 [SUBSCRIPTION] Server API response:', {
        status: response.status,
        data: data
      });
      
      if (data.subscriptionStatus) {
        console.log('🔄 [SUBSCRIPTION] Updating subscription status from server:', data.subscriptionStatus);
        setSubscriptionStatus(data.subscriptionStatus);
      } else {
        console.log('ℹ️ [SUBSCRIPTION] No subscription status in server response');
      }
    } catch (error) {
      console.error('❌ [SUBSCRIPTION] Error checking subscription status:', error);
    } finally {
      console.log('✅ [SUBSCRIPTION] Subscription check completed, setting loading to false');
      setSubscriptionLoading(false);
    }
  };

  // Load user session
  useEffect(() => {
    console.log('🔍 [ADMIN] Starting user session check...');
    const validSession = checkSessionValidity();
    console.log('🔍 [ADMIN] Session validation result:', validSession);
    
    if (validSession) {
      setCurrentUser(validSession);
      console.log('✅ [ADMIN] Current User loaded:', {
        id: validSession.id,
        email: validSession.email,
        plan: validSession.plan,
        plan_status: validSession.plan_status,
        subscription_status: validSession.subscription_status,
        stripe_customer_id: validSession.stripe_customer_id,
        trial_end_date: validSession.trial_end_date,
        email_confirm: validSession.email_confirm
      });
      
      // Si es usuario maestro, redirigir al panel maestro
      if (validSession.plan === 'master') {
        console.log('🔄 [ADMIN] Master user detected, redirecting to master admin...');
        navigate('/master-admin');
        return;
      }
      
      // Check subscription status
      console.log('🔍 [ADMIN] Starting subscription status check for user:', validSession.id);
      checkUserSubscriptionStatus(validSession.id);
    } else {
      console.log('❌ [ADMIN] No valid session found, redirecting to login...');
      navigate('/login');
    }
  }, [navigate]);

  // Load all data when a user is present
  useEffect(() => {
    if (currentUser) {
      loadAllData();
      // Initialize edit profile form with current user data
      setEditProfileForm({
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        city: currentUser.city || '',
        state: currentUser.state || ''
      });
    }
  }, [currentUser]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadDonationCampaigns(),
      loadAdoptionCases(),
      loadShelters(),
      loadShelterEvents(),
      loadAdoptionRequests(),
    ]);
    setLoading(false);
  };

  /* ------------------------------------------------------------------------
   * Data loaders
   * --------------------------------------------------------------------- */
  const loadDonationCampaigns = async () => {
    try {
      const { data, error } = await adminOperation('select', 'donation_campaigns', {
        user_id: currentUser?.id,
      });
      if (error) throw error;
      setDonationCases(data || []);
    } catch (err) {
      console.error('Error loading donation campaigns', err);
    }
  };

  const loadAdoptionCases = async () => {
    try {
      const { data, error } = await adminOperation('select', 'adoption_cases', {
        user_id: currentUser?.id,
      });
      if (error) throw error;
      setAdoptionCases(data || []);
    } catch (err) {
      console.error('Error loading adoption cases', err);
    }
  };

  const loadShelters = async () => {
    try {
      const { data, error } = await adminOperation('select', 'shelters', {
        user_id: currentUser?.id,
      });
      if (error) throw error;
      setShelters(data || []);
    } catch (err) {
      console.error('Error loading shelters', err);
    }
  };

  const loadAdoptionRequests = async () => {
    try {
      const { data, error } = await adminOperation('select', 'adoption_requests', {
        user_id: currentUser?.id,
      });
      if (error) throw error;
      setAdoptionRequests(data || []);
    } catch (err) {
      console.error('Error loading adoption requests', err);
    }
  };

  const loadShelterEvents = async () => {
    try {
      const { data, error } = await adminOperation('select', 'shelter_events', {
        user_id: currentUser?.id,
      });
      if (error) throw error;
      setShelterEvents(data || []);
    } catch (err) {
      console.error('Error loading shelter events', err);
    }
  };

  /* ------------------------------------------------------------------------
   * Form submit - Updated for multiple images
   * --------------------------------------------------------------------- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    // Prevenir envío si se están subiendo imágenes
    if (uploadingImage) {
      alert('Por favor espera a que terminen de subirse las imágenes');
      return;
    }

    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());

    try {
      // Usar las imágenes de imagePreview directamente
      const images = [...imagePreview];

      let table = '';
      let operationData: any = {};

      if (modalType === 'donacion') {
        table = 'donation_campaigns';
        operationData = {
          user_id: currentUser.id,
          title: data.title as string,
          description: data.description as string,
          image_url: images[0] || null,
          image_url_2: images[1] || null,
          image_url_3: images[2] || null,
          image_url_4: images[3] || null,
          goal: parseFloat(data.goal as string),
          raised: parseFloat(data.raised as string) || 0,
          urgent: data.urgent === 'on',
          active: true,
          bank_info: data.bank_info as string,
          payment_button1_name: data.payment_button1_name || null,
          payment_button1_url: data.payment_button1_url || null,
          payment_button1_enabled: data.payment_button1_enabled === 'on',
          payment_button2_name: data.payment_button2_name || null,
          payment_button2_url: data.payment_button2_url || null,
          payment_button2_enabled: data.payment_button2_enabled === 'on',
          contact_phone: data.contact_phone || null,
          whatsapp_link: data.whatsapp_link || null,
        };
      } else if (modalType === 'adopcion') {
        table = 'adoption_cases';
        operationData = {
          user_id: currentUser.id,
          name: data.name as string,
          age: data.age as string,
          breed: data.breed as string,
          description: data.description as string,
          image_url: images[0] || null,
          image_url_2: images[1] || null,
          image_url_3: images[2] || null,
          image_url_4: images[3] || null,
          location: data.location as string,
          vaccinated: data.vaccinated === 'on',
          sterilized: data.sterilized === 'on',
          whatsapp_phone: data.whatsapp_phone || null,
          contact_phone: data.contact_phone || null,
          adopted: false,
          requirement_1: data.requirement_1 || null,
          requirement_2: data.requirement_2 || null,
          requirement_3: data.requirement_3 || null,
          requirement_4: data.requirement_4 || null,
          requirement_5: data.requirement_5 || null,
        };
      } else if (modalType === 'refugio') {
        table = 'shelters';
        operationData = {
          user_id: currentUser.id,
          name: data.name as string,
          location: data.location as string,
          description: data.description as string,
          image_url: images[0] || null,
          image_url_2: images[1] || null,
          image_url_3: images[2] || null,
          image_url_4: images[3] || null,
          capacity: parseInt(data.capacity as string),
          current_dogs: parseInt(data.current_dogs as string),
          contact: data.contact as string,
          contact_phone: data.contact_phone || null,
          whatsapp_link: data.whatsapp_link || null,
          facebook_link: data.facebook_link || null,
          instagram_link: data.instagram_link || null,
          donation_bank_info: data.donation_bank_info || null,
          active: true,
        };
      } else if (modalType === 'evento') {
        table = 'shelter_events';

        // Obtener el shelter_id del primer refugio del usuario
        const userShelter = shelters.find(s => s.user_id === currentUser.id);
        if (!userShelter) {
          alert('Debes crear un refugio antes de poder crear eventos');
          return;
        }

        operationData = {
          user_id: currentUser.id,
          shelter_id: userShelter.id,
          title: data.title as string,
          description: data.description as string,
          image_url_1: images[0] || null,
          image_url_2: images[1] || null,
          image_url_3: images[2] || null,
          image_url_4: images[3] || null,
          image_url_5: images[4] || null,
          event_date: data.event_date as string,
          location: data.location as string,
          address: data.address as string,
          attendees_count: 0,
          active: true,
        };
      }

      console.log('Datos a enviar:', operationData);

      let result;
      if (editingItem?.id) {
        console.log('Actualizando item con ID:', editingItem.id);
        result = await adminOperation('update', table, operationData, editingItem.id);
      } else {
        console.log('Creando nuevo item');
        result = await adminOperation('insert', table, operationData);
      }

      console.log('Resultado de la operación:', result);

      if (result?.error) {
        console.error('Error en operación:', result.error);
        alert(`Error específico: ${JSON.stringify(result.error)}`);
        return;
      }

      if (!result?.data) {
        console.error('No se recibieron datos en la respuesta');
        alert('Error: No se recibieron datos en la respuesta del servidor');
        return;
      }

      // Refresh only the relevant list
      if (modalType === 'donacion') await loadDonationCampaigns();
      else if (modalType === 'adopcion') await loadAdoptionCases();
      else if (modalType === 'refugio') await loadShelters();
      else if (modalType === 'evento') await loadShelterEvents();

      closeModal();
      alert('Guardado exitosamente');
    } catch (err) {
      console.error('Error completo:', err);
      alert(`Error al guardar: ${(err as Error)?.message || 'Error desconocido'}`);
    }
  };

  /* ------------------------------------------------------------------------
   * Profile management
   * --------------------------------------------------------------------- */
  const handleEditProfile = () => {
    setShowEditProfileModal(true);
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    try {
      const result = await adminOperation('update', 'users', editProfileForm, currentUser.id);
      
      if (result?.error) {
        alert(`Error al actualizar perfil: ${result.error}`);
        return;
      }

      // Update current user session
      const updatedSession = {
        ...currentUser,
        ...editProfileForm,
        updated_at: new Date().toISOString()
      };
      
      localStorage.setItem('user_session', JSON.stringify(updatedSession));
      setCurrentUser(updatedSession);
      setShowEditProfileModal(false);
      alert('Perfil actualizado exitosamente');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error al actualizar perfil');
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    const confirmMessage = `¿Estás seguro de que quieres eliminar tu cuenta?\n\nEmail: ${currentUser.email}\n\nEsta acción no se puede deshacer y eliminará:\n- Tu perfil de usuario\n- Todas tus campañas de donación\n- Todos tus casos de adopción\n- Todos tus eventos de refugio\n\nEscribe "ELIMINAR" para confirmar:`;
    
    const userInput = prompt(confirmMessage);
    
    if (userInput !== 'ELIMINAR') {
      alert('Eliminación cancelada');
      return;
    }

    try {
      // Delete user from authentication first
      const authResult = await deleteUserFromAuth(currentUser.id);
      
      if (!authResult.success) {
        console.warn('Warning: Could not delete from auth:', authResult.error);
        // Continue with database deletion even if auth deletion fails
      }

      // Delete user account from database
      const result = await adminOperation('delete', 'users', null, currentUser.id);
      
      if (result?.error) {
        alert(`Error al eliminar cuenta: ${result.error}`);
        return;
      }

      // Clear session and redirect
      localStorage.removeItem('user_session');
      localStorage.removeItem('user_data');
      alert('Cuenta eliminada exitosamente');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Error al eliminar cuenta');
    }
  };

  /* ------------------------------------------------------------------------
   * Delete handling
   * --------------------------------------------------------------------- */
  const deleteItem = async (type: string, id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este elemento?')) return;

    try {
      let itemToDelete: any = null;
      let table = '';

      if (type === 'donacion') {
        itemToDelete = donationCases.find((i) => i.id === id);
        table = 'donation_campaigns';
      } else if (type === 'adopcion') {
        itemToDelete = adoptionCases.find((i) => i.id === id);
        table = 'adoption_cases';
      } else if (type === 'refugio') {
        itemToDelete = shelters.find((i) => i.id === id);
        table = 'shelters';
      } else if (type === 'evento') {
        itemToDelete = shelterEvents.find((i) => i.id === id);
        table = 'shelter_events';
      }

      // Delete image first (if stored in Supabase)
      if (itemToDelete?.image_url?.includes('supabase')) {
        await deleteImage(itemToDelete.image_url);
      }

      const result = await adminOperation('delete', table, null, id);
      if (result?.error) throw result.error;

      // Refresh list
      if (type === 'donacion') await loadDonationCampaigns();
      else if (type === 'adopcion') await loadAdoptionCases();
      else if (type === 'refugio') await loadShelters();
      else if (type === 'evento') await loadShelterEvents();

      alert('Eliminado exitosamente');
    } catch (err) {
      console.error('Error deleting item', err);
      alert('Error al eliminar');
    }
  };

  /* ------------------------------------------------------------------------
   * UI helpers
   * --------------------------------------------------------------------- */
  const openModal = (type: string, item?: any) => {
    setModalType(type);
    setEditingItem(item || null);
    setCurrentImageIndex(0);

    // Configurar imágenes existentes
    if (item) {
      let existingImages: string[] = [];

      if (type === 'evento') {
        existingImages = [
          item.image_url_1,
          item.image_url_2,
          item.image_url_3,
          item.image_url_4,
          item.image_url_5
        ].filter(Boolean);
      } else {
        existingImages = [
          item.image_url,
          item.image_url_2,
          item.image_url_3,
          item.image_url_4
        ].filter(Boolean);
      }

      setImagePreview(existingImages);
    } else {
      setImagePreview([]);
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setModalType('');
    setImagePreview([]);
    setCurrentImageIndex(0);
  };

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('es-MX').format(num);

  const handleLogout = () => {
    localStorage.removeItem('user_session');
    navigate('/login');
  };

  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    try {
      const result = await adminOperation('update', 'adoption_requests', { status: newStatus }, requestId);
      if (result?.error) throw result.error;
      await loadAdoptionRequests();
      alert('Estado actualizado exitosamente');
    } catch (err) {
      console.error('Error updating request status', err);
      alert('Error al actualizar el estado');
    }
  };

  // Función para manejar el dropdown
  const toggleDropdown = (requestId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    // Cerrar todos los otros dropdowns
    document.querySelectorAll('[id^="dropdown-"]').forEach(dropdown => {
      if (dropdown.id !== `dropdown-${requestId}`) {
        dropdown.classList.add('hidden');
      }
    });

    const dropdown = document.getElementById(`dropdown-${requestId}`);
    const button = event.currentTarget as HTMLElement;

    if (dropdown) {
      const isHidden = dropdown.classList.contains('hidden');

      if (isHidden) {
        // Mostrar dropdown
        dropdown.classList.remove('hidden');

        // Posicionar el dropdown correctamente
        const buttonRect = button.getBoundingClientRect();
        let left = buttonRect.right + window.scrollX - 200;

        // Ajustar si se sale de la pantalla
        if (left < 10) left = 10;
        if (left + 200 > window.innerWidth - 10) left = window.innerWidth - 210;

        dropdown.style.position = 'fixed';
        dropdown.style.top = `${buttonRect.bottom + 5}px`;
        dropdown.style.left = `${buttonRect.right - 200}px`;
        dropdown.style.zIndex = '99999';
      } else {
        // Ocultar dropdown
        dropdown.classList.add('hidden');
      }
    }
  };

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        document.querySelectorAll('[id^="dropdown-"]').forEach(dropdown => {
          dropdown.classList.add('hidden');
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  /* ------------------------------------------------------------------------
   * Image upload - Updated for multiple images
   * --------------------------------------------------------------------- */
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Obtener imágenes actuales
    const currentImages = imagePreview.length;
    const maxImages = modalType === 'evento' ? 5 : 4;
    const totalImages = currentImages + files.length;

    if (totalImages > maxImages) {
      alert(`Solo puedes subir máximo ${maxImages} fotos. Actualmente tienes ${currentImages} foto(s).`);
      return;
    }

    // Validar cada archivo
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona solo archivos de imagen válidos');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Cada imagen debe ser menor a 5MB');
        return;
      }
    }

    setUploadingImage(true);

    try {
      const folder =
        modalType === 'donacion'
          ? 'donations'
          : modalType === 'adopcion'
          ? 'adoptions'
          : modalType === 'refugio'
          ? 'shelters'
          : modalType === 'evento'
          ? 'events'
          : 'general';

      const uploadPromises = files.map(async (file) => {
        const uploadedUrl = await uploadImage(file, folder);
        return uploadedUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const validUrls = uploadedUrls.filter(url => url !== null);

      if (validUrls.length > 0) {
        // Agregar las nuevas URLs a la vista previa
        setImagePreview(prev => [...prev, ...validUrls]);
      } else {
        alert('Error al subir las imágenes');
      }
    } catch (err) {
      console.error('Error uploading images', err);
      alert('Error al subir las imágenes');
    } finally {
      setUploadingImage(false);
    }
  };

  // Función para remover imagen de la vista previa
  const removeImage = (indexToRemove: number) => {
    setImagePreview(prev => prev.filter((_, index) => index !== indexToRemove));
    // Si removemos la imagen actual y hay más imágenes, ajustar el índice
    if (currentImageIndex >= imagePreview.length - 1 && imagePreview.length > 1) {
      setCurrentImageIndex(Math.max(0, currentImageIndex - 1));
    }
  };

  /* ------------------------------------------------------------------------
   * Render
   * --------------------------------------------------------------------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin mb-4"></i>
          <p className="text-gray-600">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  // Show loading while checking subscription status
  if (subscriptionLoading && currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando estado de suscripción...</p>
        </div>
      </div>
    );
  }

  // Check if trial has expired (0 or negative days)
  const isTrialExpired = (trialEndDate: string | null): boolean => {
    if (!trialEndDate) return false;
    
    const endDate = new Date(trialEndDate);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    console.log('Trial end check:', {
      trialEndDate,
      diffDays,
      isEndingSoon: diffDays <= 4,
      isExpired: diffDays <= 0
    });
    
    // Show payment required ONLY if trial has expired
    return diffDays <= 0;
  };
  
  if ((subscriptionStatus === 'past_due' || 
       (subscriptionStatus === 'trialing' && isTrialExpired(trialEndDate)) ||
       (!currentUser?.plan || (currentUser?.plan === 'trial' && (!subscriptionStatus || subscriptionStatus === 'inactive')))) && currentUser) {
    console.log('🚨 [PAYMENT_CHECK] Payment required - showing PaymentRequired component');
    return (
      <PaymentRequired 
        userId={currentUser.id}
        onUpgradeClick={async (plan) => {
          try {
            console.log('Creating checkout session for:', {
              uid: currentUser.id,
              customer_email: currentUser.email,
              plan: plan
            });

            const response = await fetch('https://huellitas-unidas-server.vercel.app/api/create-checkout-session', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                uid: currentUser.id,
                customer_email: currentUser.email || '',
                plan: plan,
                priceId: 'price_1SFidZH4z73AhjcjKL6d2fyJ' // Premium plan price ID
              }),
            });

            console.log('Checkout response status:', response.status);
            const data = await response.json();
            console.log('Checkout response data:', data);

            if (!response.ok) {
              if (data.error === "User already has an active subscription.") {
                throw new Error("Ya tienes una suscripción activa. Si necesitas ayuda, contacta al soporte.");
              }
              throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
            }
            
            if (data.url) {
              // Keep loading state active during redirect
              window.location.href = data.url;
              
              // Set a timeout to stop loading if redirect fails
              setTimeout(() => {
                // This will only execute if the redirect doesn't happen
                console.log('Redirect timeout - user may have stayed on page');
              }, 10000); // 10 seconds timeout
              
              return;
            } else {
              throw new Error('No se pudo crear la sesión de pago - URL no recibida');
            }
          } catch (error) {
            console.error('Error upgrading subscription:', error);
            alert(`Error al procesar la actualización: ${error instanceof Error ? error.message : 'Error desconocido'}. Por favor, inténtalo de nuevo.`);
          }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Subscription Notification */}
      {currentUser && subscriptionStatus !== 'past_due' && !(subscriptionStatus === 'trialing' && isTrialExpired(trialEndDate)) && (
        <SubscriptionNotification 
          userId={currentUser.id}
          onUpgradeClick={() => {
            // Scroll to subscription section or trigger upgrade
            const subscriptionElement = document.querySelector('[data-subscription-section]');
            if (subscriptionElement) {
              subscriptionElement.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          onRetryPaymentClick={() => {
            // Handle payment retry - could open payment modal or redirect to payment page
            console.log('Retry payment clicked');
            // You can implement payment retry logic here
          }}
        />
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <i className="ri-admin-fill text-white text-lg"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Panel Administrador</h1>
                <p className="text-sm text-gray-500">{currentUser?.shelter_name || 'Huellitas Unidas'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                <i className="ri-home-line"></i>
                <span>Volver al Sitio</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-800 transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-line"></i>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('donaciones')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'donaciones'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-heart-line mr-2"></i>
              Campañas de Donación ({donationCases.length})
            </button>

            <button
              onClick={() => setActiveTab('adopciones')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'adopciones'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-home-heart-line mr-2"></i>
              Adopciones ({adoptionCases.length})
            </button>

            <button
              onClick={() => setActiveTab('refugios')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'refugios'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-building-line mr-2"></i>
              Refugios ({shelters.length})
            </button>

            <button
              onClick={() => setActiveTab('eventos')}
              className={`py-4 px-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'eventos'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-calendar-event-line mr-2"></i>
              Eventos Próximos ({shelterEvents.length})
            </button>

            <button
              onClick={() => setActiveTab('solicitudes')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'solicitudes'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-file-list-line mr-2"></i>
              Solicitudes de Adopción ({adoptionRequests.length})
            </button>

            <button
              onClick={() => setActiveTab('perfil')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'perfil'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-user-settings-line mr-2"></i>
              Perfil y Suscripción
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ----------------------  DONATIONS TAB  ---------------------- */}
        {activeTab === 'donaciones' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Campañas de Donación</h2>
              <button
                onClick={() => openModal('donacion')}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
              >
                <i className="ri-add-line mr-2"></i> Nueva Campaña
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {donationCases.map((item) => {
                const images = [item.image_url, item.image_url_2, item.image_url_3, item.image_url_4].filter(Boolean);
                return (
                  <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="relative">
                      <img
                        src={images[0] || '/placeholder-image.jpg'}
                        alt={item.title}
                        className="w-full h-48 object-cover"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {images.length}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">{item.description}</p>

                      <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                        <span>Meta: ${formatNumber(item.goal)}</span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div
                          className="bg-orange-500 h-2 rounded-full"
                          style={{ width: `${Math.min(100, (item.raised / item.goal) * 100)}%` }}
                        ></div>
                      </div>

                      <div className="flex justify-between items-center text-sm">
                        <span className="text-orange-600 font-medium">
                          Recaudado: ${formatNumber(item.raised)}
                        </span>
                      </div>

                      <div className="flex justify-between mt-4">
                        <button
                          onClick={() => openModal('donacion', item)}
                          className="text-orange-600 hover:text-orange-800"
                        >
                          <i className="ri-edit-line mr-1"></i>Editar
                        </button>
                        <button
                          onClick={() => deleteItem('donacion', item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {donationCases.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <i className="ri-heart-line text-4xl text-orange-300 mb-4"></i>
                  <p className="text-gray-500">No hay campañas de donación creadas</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ----------------------  ADOPTIONS TAB  ---------------------- */}
        {activeTab === 'adopciones' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Casos de Adopción</h2>
              <button
                onClick={() => openModal('adopcion')}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                <i className="ri-add-line mr-2"></i> Nuevo Caso
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {adoptionCases.map((item) => {
                const images = [item.image_url, item.image_url_2, item.image_url_3, item.image_url_4].filter(Boolean);
                return (
                  <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="relative">
                      <img
                        src={images[0] || '/placeholder-image.jpg'}
                        alt={item.name}
                        className="w-full h-48 object-cover"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {images.length}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            item.adopted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {item.adopted ? 'Adoptado' : 'Disponible'}
                        </span>
                      </div>

                      <p className="text-sm text-gray-500 mb-1">
                        {item.age} • {item.breed}
                      </p>

                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">{item.description}</p>

                      <p className="text-sm text-gray-500 mb-4">
                        <i className="ri-map-pin-line mr-1"></i>
                        {item.location}
                      </p>

                      <div className="flex space-x-4 mb-4">
                        <span className={`flex items-center text-sm ${item.vaccinated ? 'text-green-600' : 'text-gray-400'}`}>
                          <i className={`ri-${item.vaccinated ? 'check' : 'close'}-line mr-1`}></i>
                          Vacunado
                        </span>

                        <span className={`flex items-center text-sm ${item.sterilized ? 'text-green-600' : 'text-gray-400'}`}>
                          <i className={`ri-${item.sterilized ? 'check' : 'close'}-line mr-1`}></i>
                          Esterilizado
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={() => openModal('adopcion', item)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <i className="ri-edit-line mr-1"></i>Editar
                        </button>
                        <button
                          onClick={() => deleteItem('adopcion', item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {adoptionCases.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <i className="ri-home-heart-line text-4xl text-blue-300 mb-4"></i>
                  <p className="text-gray-500">No hay casos de adopción registrados</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ----------------------  SHELTERS TAB  ---------------------- */}
        {activeTab === 'refugios' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Refugios</h2>
              <button
                onClick={() => openModal('refugio')}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
              >
                <i className="ri-add-line mr-2"></i> Nuevo Refugio
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {shelters.map((item) => {
                const images = [item.image_url, item.image_url_2, item.image_url_3, item.image_url_4].filter(Boolean);
                return (
                  <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="relative">
                      <img
                        src={images[0] || '/placeholder-image.jpg'}
                        alt={item.name}
                        className="w-full h-48 object-cover"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {images.length}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{item.name}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">{item.description}</p>

                      <p className="text-sm text-gray-500 mb-2">
                        <i className="ri-map-pin-line mr-1"></i>
                        {item.location}
                      </p>

                      <p className="text-sm text-gray-500 mb-4">
                        <i className="ri-account-circle-line mr-1"></i>
                        {item.contact}
                      </p>

                      <div className="flex justify-between items-center text-sm mb-4">
                        <span className="text-gray-600">Capacidad:</span>
                        <span className="font-medium">
                          {item.current_dogs}/{item.capacity} perros
                        </span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{
                            width: `${Math.min(100, (item.current_dogs / item.capacity) * 100)}%`,
                          }}
                        ></div>
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={() => openModal('refugio', item)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <i className="ri-edit-line mr-1"></i>Editar
                        </button>
                        <button
                          onClick={() => deleteItem('refugio', item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {shelters.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <i className="ri-building-line text-4xl text-green-300 mb-4"></i>
                  <p className="text-gray-500">No hay refugios registrados</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ----------------------  EVENTS TAB  ---------------------- */}
        {activeTab === 'eventos' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Eventos Próximos</h2>
              <button
                onClick={() => {
                  if (shelters.length === 0) {
                    alert('Debes crear un refugio antes de poder crear eventos');
                    return;
                  }
                  openModal('evento');
                }}
                className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
              >
                <i className="ri-add-line mr-2"></i> Nuevo Evento
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {shelterEvents.map((item) => {
                const images = [item.image_url_1, item.image_url_2, item.image_url_3, item.image_url_4, item.image_url_5].filter(Boolean);
                const eventDate = new Date(item.event_date);
                const isUpcoming = eventDate > new Date();

                return (
                  <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="relative">
                      <img
                        src={images[0] || 'https://readdy.ai/api/search-image?query=Animal%20shelter%20community%20event%20with%20volunteers%20and%20rescued%20dogs%2C%20outdoor%20gathering%20with%20people%20helping%20animals%2C%20warm%20community%20atmosphere%20with%20banners%20and%20activities%20for%20pet%20adoption%20awareness&width=400&height=280&seq=event-default&orientation=landscape'}
                        alt={item.title}
                        className="w-full h-56 object-cover object-center"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {images.length}
                        </div>
                      )}
                      <div className={`absolute top-2 left-2 px-3 py-1 rounded text-xs font-semibold ${
                        isUpcoming ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                      }`}>
                        {isUpcoming ? 'Próximo' : 'Pasado'}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">{item.description}</p>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <i className="ri-calendar-line mr-2 text-purple-500"></i>
                          <span>{eventDate.toLocaleDateString('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</span>
                        </div>

                        <div className="flex items-center text-sm text-gray-500">
                          <i className="ri-map-pin-line mr-2 text-red-500"></i>
                          <span>{item.location}</span>
                        </div>

                        <div className="flex items-center text-sm text-gray-500">
                          <i className="ri-group-line mr-2 text-blue-500"></i>
                          <span>{item.attendees_count} asistentes confirmados</span>
                        </div>
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={() => openModal('evento', item)}
                          className="text-purple-600 hover:text-purple-800"
                        >
                          <i className="ri-edit-line mr-1"></i>Editar
                        </button>
                        <button
                          onClick={() => navigate(`/event/${item.id}/statistics`)}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <i className="ri-bar-chart-line mr-1"></i>Estadísticas
                        </button>
                        <button
                          onClick={() => deleteItem('evento', item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {shelterEvents.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <i className="ri-calendar-event-line text-4xl text-purple-300 mb-4"></i>
                  <p className="text-gray-500">No hay eventos programados</p>
                  {shelters.length === 0 && (
                    <p className="text-sm text-gray-400 mt-2">Crea un refugio primero para poder agregar eventos</p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ----------------------  REQUESTS TAB  ---------------------- */}
        {activeTab === 'solicitudes' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Solicitudes de Adopción</h2>
            </div>

            {adoptionRequests.length > 0 ? (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Solicitante
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contacto
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ocupación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Perrito Interesado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>

                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {adoptionRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{request.applicant_name}</div>
                              <div className="text-sm text-gray-500">{request.applicant_city}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.applicant_phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.applicant_occupation}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <i className="ri-heart-fill text-red-500 mr-2"></i>
                              <div className="text-sm font-medium text-gray-900">{request.dog_name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                request.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : request.status === 'attended'
                                  ? 'bg-blue-100 text-blue-800'
                                  : request.status === 'approved'
                                  ? 'bg-green-100 text-green-800'
                                  : request.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {request.status === 'pending'
                                ? 'Pendiente'
                                : request.status === 'attended'
                                ? 'Atendido'
                                : request.status === 'approved'
                                ? 'Aprobada'
                                : request.status === 'rejected'
                                ? 'Rechazada'
                                : 'Desconocido'}
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="dropdown-container relative inline-block text-left">
                              <button
                                onClick={(e) => toggleDropdown(request.id, e)}
                                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors cursor-pointer border border-gray-200 shadow-sm"
                              >
                                <i className="ri-more-fill text-lg"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="ri-file-list-line text-4xl text-indigo-300 mb-4"></i>
                <p className="text-gray-500">No hay solicitudes de adopción</p>
              </div>
            )}
          </section>
        )}

        {/* ----------------------  PROFILE TAB  ---------------------- */}
        {activeTab === 'perfil' && (
          <section>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Perfil y Suscripción</h2>
              <p className="text-gray-600">Gestiona tu perfil y suscripción</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Profile Card */}
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-line text-2xl text-orange-500"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {currentUser?.shelter_name || 'Usuario'}
                    </h3>
                    <p className="text-gray-600">{currentUser?.email}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Refugio:</span>
                    <span className="font-medium text-gray-800">{currentUser?.shelter_name}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Ciudad:</span>
                    <span className="font-medium text-gray-800">{currentUser?.city}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Estado:</span>
                    <span className="font-medium text-gray-800">{currentUser?.state}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Teléfono:</span>
                    <span className="font-medium text-gray-800">{currentUser?.phone}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Miembro desde:</span>
                    <span className="font-medium text-gray-800">
                      {currentUser?.created_at ? new Date(currentUser.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleEditProfile}
                    className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Editar Perfil
                  </button>
                </div>
              </div>

              {/* Subscription Management */}
              <div data-subscription-section>
                {currentUser && (
                  <SubscriptionManager 
                    userId={currentUser.id}
                    onSubscriptionUpdate={(subscription) => {
                      console.log('Subscription updated:', subscription);
                      // You can add additional logic here if needed
                    }}
                  />
                )}
              </div>
            </div>

            {/* Account Actions */}
            <div className="mt-8 bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Acciones de Cuenta</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    if (confirm('¿Estás seguro de que quieres cerrar sesión?')) {
                      localStorage.removeItem('user_session');
                      localStorage.removeItem('user_data');
                      navigate('/login');
                    }
                  }}
                  className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <i className="ri-logout-box-line mr-2 text-gray-600"></i>
                  <span className="text-gray-700">Cerrar Sesión</span>
                </button>

                <button
                  onClick={handleDeleteAccount}
                  className="flex items-center justify-center px-4 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <i className="ri-delete-bin-line mr-2"></i>
                  <span>Eliminar Cuenta</span>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Dropdowns Portal - Renderizados al final del body */}
        {adoptionRequests.map((request) => (
          <div
            key={`dropdown-${request.id}`}
            id={`dropdown-${request.id}`}
            className="hidden fixed bg-white rounded-lg shadow-2xl ring-1 ring-gray-300 border min-w-[200px] py-2"
            style={{ zIndex: 99999 }}
          >
            {request.status === 'pending' && (
              <>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'attended');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer border-b border-gray-100"
                >
                  <i className="ri-eye-line mr-3 text-blue-500 text-base"></i>
                  <span className="font-medium">Marcar como Atendido</span>
                </button>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'approved');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors cursor-pointer border-b border-gray-100"
                >
                  <i className="ri-check-double-line mr-3 text-green-500 text-base"></i>
                  <span className="font-medium">Aprobar Adopción</span>
                </button>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'rejected');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line mr-3 text-red-500 text-base"></i>
                  <span className="font-medium">Rechazar Solicitud</span>
                </button>
              </>
            )}
            {request.status === 'attended' && (
              <>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'approved');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors cursor-pointer border-b border-gray-100"
                >
                  <i className="ri-check-double-line mr-3 text-green-500 text-base"></i>
                  <span className="font-medium">Aprobar Adopción</span>
                </button>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'rejected');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors cursor-pointer border-b border-gray-100"
                >
                  <i className="ri-close-line mr-3 text-red-500 text-base"></i>
                  <span className="font-medium">Rechazar Solicitud</span>
                </button>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'pending');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 transition-colors cursor-pointer"
                >
                  <i className="ri-time-line mr-3 text-yellow-500 text-base"></i>
                  <span className="font-medium">Volver a Pendiente</span>
                </button>
              </>
            )}
            {(request.status === 'approved' || request.status === 'rejected') && (
              <>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'pending');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 transition-colors cursor-pointer border-b border-gray-100"
                >
                  <i className="ri-time-line mr-3 text-yellow-500 text-base"></i>
                  <span className="font-medium">Volver a Pendiente</span>
                </button>
                <button
                  onClick={() => {
                    updateRequestStatus(request.id, 'attended');
                    document.getElementById(`dropdown-${request.id}`)?.classList.add('hidden');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer"
                >
                  <i className="ri-eye-line mr-3 text-blue-500 text-base"></i>
                  <span className="font-medium">Marcar como Atendido</span>
                </button>
              </>
            )}
            
            <div className="border-t border-gray-200 mt-2 pt-2">
              <div className="px-4 py-2 text-xs text-gray-500 text-center">
                Sin opción de eliminar - Registro permanente
              </div>
            </div>
          </div>
        ))}

      </main>

      {/* ----------------------  MODAL  ---------------------- */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {editingItem ? 'Editar' : 'Crear'}{' '}
                  {modalType === 'donacion'
                    ? 'Campaña de Donación'
                    : modalType === 'adopcion'
                    ? 'Caso de Adopción'
                    : modalType === 'refugio'
                    ? 'Refugio'
                    : modalType === 'evento'
                    ? 'Evento'
                    : ''}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {/* --------------------  DONATION FORM  -------------------- */}
                {modalType === 'donacion' && (
                  <>
                    {/* Title */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Título *
                      </label>
                      <input
                        name="title"
                        defaultValue={editingItem?.title || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Título de la campaña"
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción *
                      </label>
                      <textarea
                        name="description"
                        defaultValue={editingItem?.description || ''}
                        required
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Descripción de la campaña"
                      ></textarea>
                    </div>

                    {/* Goal */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Meta (MXN) *
                      </label>
                      <input
                        name="goal"
                        type="number"
                        min="1"
                        step="0.01"
                        defaultValue={editingItem?.goal || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Cantidad objetivo"
                      />
                    </div>

                    {/* Raised */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cantidad Recaudada (MXN) *
                      </label>
                      <input
                        name="raised"
                        type="number"
                        min="0"
                        step="0.01"
                        defaultValue={editingItem?.raised || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    {/* Urgent */}
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          name="urgent"
                          type="checkbox"
                          defaultChecked={editingItem?.urgent}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Campaña Urgente</span>
                      </label>
                    </div>

                    {/* Bank Info */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Información Bancaria *
                      </label>
                      <textarea
                        name="bank_info"
                        defaultValue={editingItem?.bank_info || ''}
                        required
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Datos bancarios para donaciones"
                      ></textarea>
                    </div>

                    {/* Payment Button 1 */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Botón de Pago 1
                      </label>
                      <input
                        name="payment_button1_name"
                        defaultValue={editingItem?.payment_button1_name || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 mb-2"
                        placeholder="Nombre del botón (ej: PayPal)"
                      />
                      <input
                        name="payment_button1_url"
                        defaultValue={editingItem?.payment_button1_url || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 mb-2"
                        placeholder="URL del botón de pago"
                      />
                      <label className="flex items-center">
                        <input
                          name="payment_button1_enabled"
                          type="checkbox"
                          defaultChecked={editingItem?.payment_button1_enabled}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Habilitar Botón 1</span>
                      </label>
                    </div>

                    {/* Payment Button 2 */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Botón de Pago 2
                      </label>
                      <input
                        name="payment_button2_name"
                        defaultValue={editingItem?.payment_button2_name || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 mb-2"
                        placeholder="Nombre del botón (ej: Stripe)"
                      />
                      <input
                        name="payment_button2_url"
                        defaultValue={editingItem?.payment_button2_url || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 mb-2"
                        placeholder="URL del botón de pago"
                      />
                      <label className="flex items-center">
                        <input
                          name="payment_button2_enabled"
                          type="checkbox"
                          defaultChecked={editingItem?.payment_button2_enabled}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Habilitar Botón 2</span>
                      </label>
                    </div>

                    {/* Contact Phone */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Teléfono de Contacto
                      </label>
                      <input
                        name="contact_phone"
                        defaultValue={editingItem?.contact_phone || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Número de teléfono para contacto"
                      />
                    </div>

                    {/* WhatsApp Link */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Enlace de WhatsApp
                      </label>
                      <input
                        name="whatsapp_link"
                        defaultValue={editingItem?.whatsapp_link || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="https://wa.me/1234567890"
                      />
                    </div>
                  </>
                )}

                {/* --------------------  ADOPTION FORM  -------------------- */}
                {modalType === 'adopcion' && (
                  <>
                    {/* Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre *
                      </label>
                      <input
                        name="name"
                        defaultValue={editingItem?.name || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nombre del perrito"
                      />
                    </div>

                    {/* Age */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Edad *
                      </label>
                      <input
                        name="age"
                        defaultValue={editingItem?.age || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Edad aproximada"
                      />
                    </div>

                    {/* Breed */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Raza *
                      </label>
                      <input
                        name="breed"
                        defaultValue={editingItem?.breed || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Raza del perrito"
                      />
                    </div>

                    {/* Historia - Cambio de Descripción a Historia y obligatorio */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Historia *
                      </label>
                      <textarea
                        name="description"
                        defaultValue={editingItem?.description || ''}
                        required
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Escribe la historia completa de este perrito - este campo es obligatorio"
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">
                        La historia aparecerá en la vista pública y es obligatoria para completar el caso
                      </p>
                    </div>

                    {/* Location */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ubicación *
                      </label>
                      <input
                        name="location"
                        defaultValue={editingItem?.location || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ciudad o región"
                      />
                    </div>

                    {/* Vaccinated */}
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          name="vaccinated"
                          type="checkbox"
                          defaultChecked={editingItem?.vaccinated}
                          className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Vacunado</span>
                      </label>
                    </div>

                    {/* Sterilized */}
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          name="sterilized"
                          type="checkbox"
                          defaultChecked={editingItem?.sterilized}
                          className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Esterilizado</span>
                      </label>
                    </div>

                    {/* WhatsApp Phone - Ahora obligatorio */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Link de WhatsApp *
                      </label>
                      <input
                        name="whatsapp_phone"
                        defaultValue={editingItem?.whatsapp_phone || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://wa.me/5215512345678 o número de WhatsApp"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Puedes poner el enlace completo de WhatsApp o solo el número
                      </p>
                    </div>

                    {/* Contact Phone - Ahora obligatorio */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Teléfono de Contacto *
                      </label>
                      <input
                        name="contact_phone"
                        defaultValue={editingItem?.contact_phone || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Número de teléfono para contacto"
                      />
                    </div>

                    {/* Requisitos de Adopción - Nuevos campos */}
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">
                        Requisitos de Adopción
                      </h4>
                      <p className="text-xs text-gray-500 mb-4">
                        Define los requisitos específicos para adoptar a este perrito. Deja en blanco los que no apliquen.
                      </p>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Requisito 1
                          </label>
                          <input
                            name="requirement_1"
                            defaultValue={editingItem?.requirement_1 || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Ser mayor de 18 años y tener identificación oficial"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Requisito 2
                          </label>
                          <input
                            name="requirement_2"
                            defaultValue={editingItem?.requirement_2 || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Contar con espacio adecuado y seguro"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Requisito 3
                          </label>
                          <input
                            name="requirement_3"
                            defaultValue={editingItem?.requirement_3 || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Compromiso de cuidados veterinarios regulares"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Requisito 4
                          </label>
                          <input
                            name="requirement_4"
                            defaultValue={editingItem?.requirement_4 || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Tiempo y dedicación para el cuidado diario"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Requisito 5
                          </label>
                          <input
                            name="requirement_5"
                            defaultValue={editingItem?.requirement_5 || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ej: Firmar contrato de adopción responsable"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* --------------------  SHELTER FORM  -------------------- */}
                {modalType === 'refugio' && (
                  <>
                    {/* Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre del Refugio *
                      </label>
                      <input
                        name="name"
                        defaultValue={editingItem?.name || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Nombre del refugio"
                      />
                    </div>

                    {/* Location */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ubicación *
                      </label>
                      <input
                        name="location"
                        defaultValue={editingItem?.location || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Dirección o ciudad"
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción *
                      </label>
                      <textarea
                        name="description"
                        defaultValue={editingItem?.description || ''}
                        required
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Descripción del refugio"
                      ></textarea>
                    </div>

                    {/* Capacity */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Capacidad Máxima *
                      </label>
                      <input
                        name="capacity"
                        type="number"
                        min="1"
                        defaultValue={editingItem?.capacity || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Número máximo de perros"
                      />
                    </div>

                    {/* Current Dogs */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Perros Actuales *
                      </label>
                      <input
                        name="current_dogs"
                        type="number"
                        min="0"
                        defaultValue={editingItem?.current_dogs || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Número de perros actuales"
                      />
                    </div>

                    {/* Contact */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contacto *
                      </label>
                      <input
                        name="contact"
                        defaultValue={editingItem?.contact || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Persona responsable"
                      />
                    </div>

                    {/* Información de Contacto - Nueva sección */}
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">
                        Información de Contacto
                      </h4>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Teléfono de Contacto
                          </label>
                          <input
                            name="contact_phone"
                            defaultValue={editingItem?.contact_phone || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="Número de teléfono para contacto"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            WhatsApp
                          </label>
                          <input
                            name="whatsapp_link"
                            defaultValue={editingItem?.whatsapp_link || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="https://wa.me/5215512345678 o número de WhatsApp"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Redes Sociales - Nueva sección */}
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">
                        Redes Sociales
                      </h4>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Facebook
                          </label>
                          <input
                            name="facebook_link"
                            defaultValue={editingItem?.facebook_link || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="https://facebook.com/tu-refugio"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Instagram
                          </label>
                          <input
                            name="instagram_link"
                            defaultValue={editingItem?.instagram_link || ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="https://instagram.com/tu-refugio"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Información de Donación - Nueva sección */}
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-800 mb-3 border-b border-gray-200 pb-2">
                        Información para Donaciones
                      </h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cuenta Bancaria para Donaciones
                        </label>
                        <textarea
                          name="donation_bank_info"
                          defaultValue={editingItem?.donation_bank_info || ''}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Información bancaria para recibir donaciones al refugio&#10;Ejemplo:&#10;Banco: BBVA&#10;Cuenta: 1234567890&#10;CLABE: 012345678901234567&#10;Titular: Refugio Ejemplo A.C."
                        ></textarea>
                        <p className="text-xs text-gray-500 mt-1">
                          Esta información se mostrará cuando alguien quiera donar directamente al refugio
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* --------------------  EVENT FORM  -------------------- */}
                {modalType === 'evento' && (
                  <>
                    {/* Title */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Título del Evento *
                      </label>
                      <input
                        name="title"
                        defaultValue={editingItem?.title || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Nombre del evento"
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción del Evento *
                      </label>
                      <textarea
                        name="description"
                        defaultValue={editingItem?.description || ''}
                        required
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Describe el evento, actividades, objetivos, etc."
                      ></textarea>
                    </div>

                    {/* Event Date */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha y Hora del Evento *
                      </label>
                      <input
                        name="event_date"
                        type="datetime-local"
                        defaultValue={editingItem?.event_date ? new Date(editingItem.event_date).toISOString().slice(0, 16) : ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    {/* Location */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Lugar del Evento *
                      </label>
                      <input
                        name="location"
                        defaultValue={editingItem?.location || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Nombre del lugar (ej: Parque Central, Centro Comunitario)"
                      />
                    </div>

                    {/* Address */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dirección Completa *
                      </label>
                      <textarea
                        name="address"
                        defaultValue={editingItem?.address || ''}
                        required
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Dirección completa del evento con referencias"
                      ></textarea>
                    </div>
                  </>
                )}

                {/* --------------------  IMAGE UPLOAD - Updated for multiple images  -------------------- */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imágenes (Máximo {modalType === 'evento' ? '5' : '4'} fotos)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    disabled={uploadingImage || (imagePreview.length >= (modalType === 'evento' ? 5 : 4))}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puedes subir hasta {modalType === 'evento' ? '5' : '4'} imágenes. Formatos: JPG, PNG, GIF. Máximo 5MB por imagen.
                  </p>

                  {uploadingImage && (
                    <div className="mt-2 text-sm text-gray-500 flex items-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Subiendo imágenes...
                    </div>
                  )}

                  {/* Image Preview Gallery */}
                  {imagePreview.length > 0 && (
                    <div className="mt-4">
                      <div className="grid grid-cols-2 gap-2">
                        {imagePreview.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-md border"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                            {index === 0 && (
                              <div className="absolute bottom-1 left-1 bg-blue-500 text-white px-2 py-1 rounded text-xs">
                                Principal
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Navigation for multiple images */}
                      {imagePreview.length > 1 && (
                        <div className="mt-3 flex justify-center space-x-2">
                          <button
                            type="button"
                            onClick={() =>
                              setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : imagePreview.length - 1)
                            }
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                          >
                            <i className="ri-arrow-left-line"></i>
                          </button>
                          <span className="flex items-center px-3 py-2 bg-gray-100 rounded-full text-sm">
                            {currentImageIndex + 1} de {imagePreview.length}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setCurrentImageIndex(currentImageIndex < imagePreview.length - 1 ? currentImageIndex + 1 : 0)
                            }
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                          >
                            <i className="ri-arrow-right-line"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* --------------------  SUBMIT BUTTON  -------------------- */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingImage}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                      uploadingImage
                        ? 'bg-gray-400 cursor-not-allowed'
                        : modalType === 'donacion'
                        ? 'bg-orange-500 hover:bg-orange-600'
                        : modalType === 'adopcion'
                        ? 'bg-blue-500 hover:bg-blue-600'
                        : modalType === 'refugio'
                        ? 'bg-green-500 hover:bg-green-600'
                        : modalType === 'evento'
                        ? 'bg-purple-500 hover:bg-purple-600'
                        : 'bg-gray-500 hover:bg-gray-600'
                    }`}
                  >
                    {uploadingImage ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Subiendo...
                      </>
                    ) : (
                      'Guardar'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Editar Perfil</h2>
                <button
                  onClick={() => setShowEditProfileModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editProfileForm.email}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">El email no se puede modificar</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={editProfileForm.phone}
                      onChange={(e) => setEditProfileForm({...editProfileForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ciudad
                    </label>
                    <input
                      type="text"
                      value={editProfileForm.city}
                      onChange={(e) => setEditProfileForm({...editProfileForm, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado
                    </label>
                    <input
                      type="text"
                      value={editProfileForm.state}
                      onChange={(e) => setEditProfileForm({...editProfileForm, state: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditProfileModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
